<script setup>
import { computed } from 'vue';
import KanbanCardV2 from './KanbanCardV2.vue';

const props = defineProps({
  stageName: {
    type: String,
    required: true,
  },
  stageConfig: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  funnelId: {
    type: Number,
    required: false,
    default: null,
  },
  isDragOver: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'cardClick',
  'cardRemove',
  'openConversation',
  'openCrm',
  'dragStart',
  'dragOver',
  'dragLeave',
  'drop',
  'toggleChecklist',
  'assignAgent',
  'loadMore',
]);

// Helper method to get mapping for a conversation
const getConversationMapping = conversation => {
  if (conversation.funnel_mappings && conversation.funnel_mappings.length > 0) {
    return conversation.funnel_mappings[0];
  }
  return {};
};

// Computed properties
const conversationCount = computed(() => props.conversations.length);

// Calcular o valor total de todos os produtos/serviços nesta coluna
const totalValue = computed(() => {
  let total = 0;

  props.conversations.forEach(conversation => {
    const products = conversation.conversation_products || [];
    const filteredProducts = props.funnelId
      ? products.filter(
          product => !product.funnel_id || product.funnel_id === props.funnelId
        )
      : products;

    filteredProducts.forEach(product => {
      total += parseFloat(product.amount) || 0;
    });
  });

  return total;
});

// Formatar valor em moeda brasileira
const formattedTotalValue = computed(() => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalValue.value);
});

// Estilo da borda colorida da coluna
const columnBorderStyle = computed(() => {
  const color = props.stageConfig?.color || '#6366f1';
  return {
    '--column-color': color,
    '--column-color-light': `${color}15`,
    boxShadow: `inset 0 0 0 1px ${color}30`,
  };
});

// Event handlers
const handleCardClick = conversation => {
  // Passa a conversation completa para abrir o painel lateral
  emit('cardClick', conversation);
};

const handleOpenConversation = conversation => {
  emit('openConversation', conversation);
};

const handleOpenCrm = (conversation, contact) => {
  emit('openCrm', conversation, contact);
};

const handleCardRemove = conversation => {
  emit('cardRemove', conversation);
};

const handleDragStart = (conversationId, stageName, event) => {
  emit('dragStart', conversationId, stageName, event);
};

const handleDragOver = event => {
  event.preventDefault();
  emit('dragOver', event, props.stageName);
};

const handleDragLeave = event => {
  emit('dragLeave', event, props.stageName);
};

const handleDrop = event => {
  event.preventDefault();
  emit('drop', event, props.stageName);
};

const handleLoadMore = () => {
  emit('loadMore', props.stageName);
};

// Computed para mostrar texto do botão
const loadMoreText = computed(() => {
  const remaining = props.totalCount - props.conversations.length;
  if (remaining > 0) {
    return `Carregar mais (${remaining})`;
  }
  return 'Carregar mais';
});
</script>

<template>
  <div
    class="kanban-column flex flex-col rounded-lg bg-n-solid-2 min-h-full w-72 overflow-hidden relative"
    :class="[{ 'ring-2 ring-woot-400/50': isDragOver }]"
    :style="columnBorderStyle"
    :data-column-name="stageName"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <!-- Borda lateral colorida -->
    <div
      class="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
      :style="{ backgroundColor: stageConfig.color || '#6366f1' }"
    />

    <!-- Barra colorida no topo -->
    <div
      class="h-1.5 w-full"
      :style="{ backgroundColor: stageConfig.color || '#6366f1' }"
    />

    <!-- Header da Coluna -->
    <div class="px-3 py-2.5 border-b border-n-weak">
      <div class="flex items-center gap-2">
        <span class="text-sm font-bold text-n-slate-12">
          {{ conversationCount }}
        </span>
        <h3 class="text-sm font-medium text-n-slate-11">
          {{ stageConfig.label || stageName }}
        </h3>
      </div>

      <!-- Valor total (opcional, mostrar só se tiver valor) -->
      <div v-if="totalValue > 0" class="mt-1">
        <span class="text-xs font-medium text-n-teal-11">
          {{ formattedTotalValue }}
        </span>
      </div>
    </div>

    <!-- Lista de conversas -->
    <div
      class="column-content flex-1 p-2 space-y-2 overflow-y-auto"
      @dragover.prevent
      @drop="handleDrop"
    >
      <KanbanCardV2
        v-for="conversation in conversations"
        :key="conversation.id"
        :conversation="conversation"
        :stage-config="stageConfig"
        :mapping="getConversationMapping(conversation)"
        :funnel-id="funnelId"
        :stage-name="stageName"
        @click="handleCardClick(conversation)"
        @open-conversation="handleOpenConversation"
        @open-crm="handleOpenCrm"
        @remove-from-funnel="handleCardRemove"
        @drag-start="handleDragStart"
      />

      <!-- Empty state -->
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <div
        v-if="conversations.length === 0"
        class="text-center py-8 text-n-slate-10"
      >
        <p class="text-sm">Nenhuma conversa</p>
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template -->

      <!-- Load more button -->
      <button
        v-if="hasMore && conversations.length > 0"
        class="w-full py-2 px-3 mt-1 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-lg transition-colors flex items-center justify-center gap-2"
        :disabled="isLoadingMore"
        @click="handleLoadMore"
      >
        <span
          v-if="isLoadingMore"
          class="animate-spin h-3 w-3 border-2 border-n-slate-8 border-t-n-slate-11 rounded-full"
        />
        <span v-else>{{ loadMoreText }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.kanban-column {
  min-width: 280px;
  max-width: 280px;
  transition: all 0.15s ease;
  --column-color: #6366f1;
  --column-color-light: rgba(99, 102, 241, 0.08);
  border: 1px solid transparent;
  background: linear-gradient(
      to right,
      var(--column-color-light) 0%,
      transparent 30%
    ),
    linear-gradient(to bottom, var(--tw-gradient-stops));
  background-color: var(--n-solid-2);
}

/* Efeito de brilho sutil no topo */
.kanban-column::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(
    to bottom,
    var(--column-color-light) 0%,
    transparent 100%
  );
  pointer-events: none;
  border-radius: 8px 8px 0 0;
}

.kanban-column.drag-over {
  transform: scale(1.01);
}

.column-content {
  max-height: calc(100vh - 180px);
  min-height: 200px;
  position: relative;
  z-index: 1;
}

.column-content::-webkit-scrollbar {
  width: 4px;
}

.column-content::-webkit-scrollbar-track {
  background: transparent;
}

.column-content::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 2px;
}

.column-content::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
</style>
